import * as XLSX from 'xlsx';
import type { CandidateWithDetails } from '@/types';

const PROFILE_LABELS: Record<string, string> = {
  farmer: 'Farmer', hunter: 'Hunter', private: 'Private', alta_renda: 'Alta Renda',
};
const STATUS_LABELS: Record<string, string> = {
  prospeccao: 'Prospecção', r1: 'R1 - Café', r2_r3: 'R2/R3 - Entrevista',
  proposta: 'Proposta', fechado_ganho: 'Fechado - Ganho', fechado_perdido: 'Fechado - Perdido',
};
const ORIGIN_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn', indicacao_interna: 'Indicação Interna',
  evento: 'Evento', ativo: 'Ativo', headhunter: 'Headhunter',
};
const LOSS_LABELS: Record<string, string> = {
  ficou_na_base: 'Ficou na Base', foi_concorrencia: 'Foi para Concorrência',
  perfil_nao_aderente: 'Perfil Não Aderente', outro: 'Outro',
};

export function exportCandidatesToXlsx(candidates: CandidateWithDetails[]) {
  const closedStatuses = ['fechado_ganho', 'fechado_perdido'];
  const active = candidates.filter((c) => !closedStatuses.includes(c.funnel_status));

  // ── Sheet 1: Candidatos ──────────────────────────────────────────────────
  const sheetData = candidates.map((c) => ({
    'Nome': c.name,
    'Instituição Atual': c.current_institution,
    'Tempo de Casa (anos)': c.tenure_years,
    'AUM (R$)': c.aum_cents / 100,
    'ROA (%)': c.roa_bps / 100,
    'Perfil': PROFILE_LABELS[c.profile] ?? c.profile,
    'Status do Funil': STATUS_LABELS[c.funnel_status] ?? c.funnel_status,
    'Origem': ORIGIN_LABELS[c.origin] ?? c.origin,
    'Previsão de Onboarding': c.onboarding_forecast ?? '',
    'Custódia Estimada (R$)': c.estimated_custody_cents / 100,
    'Score': c.score,
    'Motivo de Perda': c.loss_reason ? (LOSS_LABELS[c.loss_reason] ?? c.loss_reason) : '',
    'Última Interação': c.last_interaction_date ?? '',
    'Empreendedorismo': c.qualitative_score?.entrepreneurship ?? '',
    'Execução': c.qualitative_score?.execution ?? '',
    'Ambição': c.qualitative_score?.ambition ?? '',
    'LinkedIn': c.linkedin_url ?? '',
    'Data Primeiro Contato': c.first_contact_date,
    'Criado em': c.created_at.slice(0, 10),
  }));

  const candidatesSheet = XLSX.utils.json_to_sheet(sheetData);
  candidatesSheet['!cols'] = [
    { wch: 30 }, { wch: 28 }, { wch: 20 }, { wch: 18 }, { wch: 10 },
    { wch: 14 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 22 },
    { wch: 8  }, { wch: 24 }, { wch: 18 }, { wch: 18 }, { wch: 12 },
    { wch: 12 }, { wch: 36 }, { wch: 20 }, { wch: 16 },
  ];

  // ── Sheet 2: Resumo ──────────────────────────────────────────────────────
  const staleThreshold = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const stale = active.filter((c) => !c.last_interaction_date || c.last_interaction_date < staleThreshold);
  const totalCustody = active.reduce((s, c) => s + (c.estimated_custody_cents ?? 0), 0);
  const avgScore = active.length > 0
    ? (active.reduce((s, c) => s + (c.score ?? 0), 0) / active.length).toFixed(1)
    : '0.0';

  const byStatus = Object.entries(
    candidates.reduce<Record<string, number>>((acc, c) => {
      acc[c.funnel_status] = (acc[c.funnel_status] ?? 0) + 1;
      return acc;
    }, {}),
  ).map(([k, v]) => ({ Métrica: STATUS_LABELS[k] ?? k, Valor: v }));

  const summaryRows = [
    { Métrica: 'Total de Candidatos Ativos', Valor: active.length },
    { Métrica: 'Total Fechado Ganho', Valor: candidates.filter((c) => c.funnel_status === 'fechado_ganho').length },
    { Métrica: 'Total Fechado Perdido', Valor: candidates.filter((c) => c.funnel_status === 'fechado_perdido').length },
    { Métrica: 'Score Médio (ativos)', Valor: avgScore },
    { Métrica: 'Custódia Estimada Total (R$)', Valor: totalCustody / 100 },
    { Métrica: 'Sem contato há mais de 10 dias', Valor: stale.length },
    { Métrica: '', Valor: '' },
    { Métrica: '--- Por Status do Funil ---', Valor: '' },
    ...byStatus,
  ];

  const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
  summarySheet['!cols'] = [{ wch: 40 }, { wch: 20 }];

  // ── Workbook ─────────────────────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, candidatesSheet, 'Candidatos');
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Resumo');

  const filename = `candidatos-${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename);
}
